import { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { SiAppstore, SiGoogleplay } from "react-icons/si";
import {
    APP_APPLE_STORE_URL,
    APP_PLAY_STORE_URL,
} from "./AppStoreLinks";
import type { MobileAppPlatform } from "../utils/mobilePlatform";
import {
    getMobileAppPlatform,
    isAppDownloadBarDismissed,
    setAppDownloadBarDismissed,
} from "../utils/mobilePlatform";

type MobileAppDownloadBarProps = {
    /** Lets the page add bottom padding so content isn’t hidden behind the bar. */
    onOpenChange?: (open: boolean) => void;
};

/**
 * Bottom sheet–style prompt (cookie-bar style) to open the native store on mobile.
 */
function MobileAppDownloadBar({ onOpenChange }: MobileAppDownloadBarProps) {
    const [platform, setPlatform] = useState<MobileAppPlatform | null>(null);

    useEffect(() => {
        if (isAppDownloadBarDismissed()) return;
        const p = getMobileAppPlatform();
        if (p) {
            setPlatform(p);
            onOpenChange?.(true);
        }
        // Intentionally once on mount; parent should pass a stable setter (e.g. useState dispatch).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dismiss = () => {
        setAppDownloadBarDismissed();
        setPlatform(null);
        onOpenChange?.(false);
    };

    if (!platform) return null;

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[60] px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none"
            role="dialog"
            aria-labelledby="mobile-app-download-title"
            aria-describedby="mobile-app-download-desc"
        >
            <div className="pointer-events-auto max-w-7xl mx-auto bg-white rounded-t-2xl border border-gray-200 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] overflow-hidden">
                <div className="flex items-start gap-3 p-4">
                    <div className="flex-1 min-w-0">
                        <p
                            id="mobile-app-download-title"
                            className="text-sm font-semibold text-gray-900 pr-2"
                        >
                            {platform === "android" && "Merr aplikacionin në Google Play"}
                            {platform === "ios" && "Merr aplikacionin për iPhone"}
                            {platform === "other" && "Merr aplikacionin në telefon"}
                        </p>
                        <p
                            id="mobile-app-download-desc"
                            className="text-xs text-gray-600 mt-1"
                        >
                            Eksperiencë më e shpejtë dhe njoftime për porositë dhe shpalljet.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 mt-3">
                            {(platform === "android" || platform === "other") && (
                                <a
                                    href={APP_PLAY_STORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#01875f] text-white text-sm font-medium hover:opacity-95 active:opacity-90"
                                >
                                    <SiGoogleplay className="text-lg" aria-hidden />
                                    Google Play
                                </a>
                            )}
                            {(platform === "ios" || platform === "other") && (
                                <a
                                    href={APP_APPLE_STORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 active:bg-gray-900"
                                >
                                    <SiAppstore className="text-lg" aria-hidden />
                                    App Store
                                </a>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={dismiss}
                            className="mt-3 text-xs font-medium text-gray-500 hover:text-[#704e37] underline-offset-2 hover:underline"
                        >
                            Jo tani
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={dismiss}
                        className="shrink-0 p-2 -mr-1 -mt-1 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#704e37]/30"
                        aria-label="Mbyll"
                    >
                        <FaTimes className="text-lg" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MobileAppDownloadBar;
